import { useState, useCallback } from 'react'

/**
 * @typedef {Object} UseModalReturn
 * @property {function} openModal - opens the given modal, takes an element of
 * T as it's only argument
 * @property {function} closeModal - closes any of the currently open modals
 * @property {function} isOpen - returns true if the given modal is currently
 * open.
 */
interface UseModalReturn<T> {
  openModal: (modal: T) => () => void
  closeModal: () => void
  isOpen: (modal: T) => boolean
}

/**
 * Useful utility for a page that may render one of many modals. This hook
 * helps to enforce the constraint that only one modal can be open at a time. It
 * does this by representing the modal's open/close state as an enum rather
 * than a bunch of true/false states. Then provides utility methods for
 * toggling each modal, and checking whether each modal is currently open.
 *
 * @deprecated Use utils/hooks/useModalState instead
 * @return {UseModalReturn}
 */
export const useModal = <T extends string>(defaultOpenModal?: T): UseModalReturn<T> => {
  const [currentModal, setCurrentModal] = useState<T | undefined>(defaultOpenModal)

  const openModal = useCallback((modal: T) => () => setCurrentModal(modal), [])
  const closeModal = useCallback(() => setCurrentModal(undefined), [])
  const isOpen = useCallback((modal: T) => currentModal === modal, [currentModal])

  return {
    openModal,
    closeModal,
    isOpen,
  }
}
